import React, { useEffect, useState } from "react";
import MyMap from "../../../../Maps/MyMap";
import MapSelection from "../../../../Maps/MapSelection";
import { getOverlayLocation } from "../../../../Maps/helpers";
import JsonCollapse from "../../../Components/JsonCollapse";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import MapGeoJsonDrawer from "../../../../Maps/MapGeoJsonDrawer";
import { useMap } from "@vis.gl/react-google-maps";

export interface MapInputProps {
  mapDrawingModes?: ("marker" | "polygon" | "rectangle" | "circle")[];
  values: any[];
  onChange: (value: any) => void;
  isMultiple?: boolean;
  isReadOnly?: boolean;
}

function parsePointFeature(value: any) {
  try {
    const feature = typeof value === "string" ? JSON.parse(value) : value;
    const coordinates = feature?.geometry?.coordinates;

    if (
      feature?.type !== "Feature" ||
      feature?.geometry?.type !== "Point" ||
      !Array.isArray(coordinates) ||
      coordinates.length < 2
    ) {
      return null;
    }

    return {
      feature,
      position: {
        lng: Number(coordinates[0]),
        lat: Number(coordinates[1]),
      },
    };
  } catch {
    return null;
  }
}

function updatePointFeature(value: any, position: google.maps.LatLngLiteral) {
  const pointFeature = parsePointFeature(value);
  if (!pointFeature) {
    return value;
  }

  return JSON.stringify({
    ...pointFeature.feature,
    geometry: {
      ...pointFeature.feature.geometry,
      coordinates: [position.lng, position.lat],
    },
  });
}

function MapDraggablePointMarkers({
  values,
  onChange,
  isReadOnly,
}: {
  values: any[];
  onChange: (value: any) => void;
  isReadOnly?: boolean;
}) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const markers = values
      .map((value, index) => {
        const pointFeature = parsePointFeature(value);
        if (!pointFeature) {
          return null;
        }

        const marker = new google.maps.Marker({
          map,
          position: pointFeature.position,
          draggable: !isReadOnly,
        });

        marker.addListener("dragend", () => {
          const position = marker.getPosition();
          if (!position) {
            return;
          }

          const newValues = [...values];
          newValues[index] = updatePointFeature(values[index], {
            lat: position.lat(),
            lng: position.lng(),
          });
          onChange(newValues);
        });

        return marker;
      })
      .filter(Boolean) as google.maps.Marker[];

    return () => {
      markers.forEach((marker) => {
        google.maps.event.clearInstanceListeners(marker);
        marker.setMap(null);
      });
    };
  }, [isReadOnly, map, onChange, values]);

  return <></>;
}

export default function MapInput({
  mapDrawingModes,
  values,
  onChange,
  isMultiple = false,
  isReadOnly = false,
}: MapInputProps) {
  const [overlay, setOverlay] = useState({
    show: false,
    position: { x: 0, y: 0 },
    content: <></>,
  });

  return (
    <>
      <div className="p-2 bg-light rounded-2">
        <div className="mb-2">
          <div className="d-flex gap-2 flex-wrap">
            <strong>Selected Locations:</strong>
            {values.map((v, i) => {
              let parsed: any = null;
              try {
                parsed = JSON.parse(v);
              } catch {
                // malformed entry — skip display
              }
              if (!parsed) return null;
              return (
                <div
                  key={i}
                  title={JSON.stringify(parsed.geometry?.coordinates)}
                  className="badge rounded-pill p-0 px-2 bg-colored"
                >
                  {parsed.geometry?.type ?? "Unknown"}
                  <button
                    type="button"
                    disabled={isReadOnly}
                    className="btn btn-small border-0 ms-2 p-0 rounded-circle text-white"
                    onClick={() => {
                      const newValues = [...values];
                      newValues.splice(i, 1);
                      onChange(newValues);
                    }}
                  >
                    <FontAwesomeIcon icon={faCircleXmark} />
                  </button>
                </div>
              );
            })}
          </div>
          <div style={{ height: 300 }} className="w-100">
            <MyMap mapTypeControl={false}>
              <MapGeoJsonDrawer
                features={values}
                setOverlay={setOverlay}
                hidePointFeatures
                shouldFitBounds={false}
                onDelete={(feature) => {
                  if (isReadOnly) {
                    return;
                  }
                  const newValues = [...values];
                  newValues.splice(feature.getProperty("index"), 1);
                  onChange(newValues);
                }}
              />
              <MapDraggablePointMarkers
                values={values}
                onChange={onChange}
                isReadOnly={isReadOnly}
              />
              {!isReadOnly && (
                <MapSelection
                  key={values.length}
                  drawingModes={
                    mapDrawingModes || ["polygon", "rectangle", "marker"]
                  }
                  setOverlay={setOverlay}
                  setMapBounds={() => {}}
                  setShowSidebar={() => {}}
                  onSelect={(shape) => {
                    const location = shape?.overlay
                      ? getOverlayLocation(shape?.overlay)
                      : null;
                    if (!location) {
                      return;
                    }

                    const fd = JSON.stringify({
                      type: "Feature",
                      geometry: location.IsShape ? location.Area : location.Point,
                    });

                    const newValues = [...(isMultiple ? values : []), fd];
                    onChange(newValues);
                  }}
                />
              )}
              {overlay.show && (
                <div
                  className="position-fixed"
                  style={{
                    top: overlay.position.y - 20,
                    left: overlay.position.x + 20,
                    zIndex: 10000,
                  }}
                >
                  {overlay.content}
                </div>
              )}
            </MyMap>
          </div>
          <JsonCollapse json={{ values }} />
        </div>
      </div>
    </>
  );
}
